<template>
    <Teleport
        v-if="isMounted"
        to="body"
    >
        <div :class="[$style.wrapper, props.adInfo.isVisible && $style.visible]">
            <div
                :id="containerId"
                :class="$style.ad"
            />
            <div :class="$style.bottom_bar">
                <img
                    :src="props.adInfo.debranding ? DollarCoinImg : FennekImg"
                    alt="fennec"
                    :class="$style.icon"
                />
                <div :class="$style.info">
                    <Typography
                        accent
                        size="s"
                        :class="$style.text"
                    >
                        {{ label }}
                    </Typography>
                    <Typography
                        v-if="subtext"
                        size="xs"
                        :class="$style.subtext"
                    >
                        {{ subtext }}
                    </Typography>
                </div>
                <Button
                    v-if="props.isAdFreeButtonShown"
                    color="lime"
                    :class="$style.no_ads_button_override"
                    interfaceTarget="no-ads-button"
                    :label="$tr('gameContainer.noAds')"
                    @click="emit('noAdsClick')"
                />
            </div>
        </div>
    </Teleport>
</template>

<script lang="ts" setup>
import { computed } from 'vue'

import Button from '@/components/Buttons/Button.vue'
import Typography from '@/components/Typography.vue'
import { useTr } from '@/composables/use-tr'
import { generateAdElementId } from '@/modules/adv/utils/generate-ad-id'
import { useIsMounted } from '@/utils/vue-hooks/use-is-mounted'

import type { AdInfo } from '../../types'
import DollarCoinImg from './images/dollar-coin.webp'
import FennekImg from './images/fennec.webp'

const emit = defineEmits<{
    noAdsClick: []
}>()

const props = defineProps<{
    adInfo: AdInfo
    isAdFreeActive: boolean
    isAdFreeButtonShown: boolean
}>()

const tr = useTr()
const containerId = generateAdElementId()
const isMounted = useIsMounted()

defineExpose({ containerId })

const label = computed(() => {
    if (props.isAdFreeActive && props.adInfo.adType === 'rewarded') {
        return tr('instreamWrapper.noAdInfoRewarded')
    }

    return props.isAdFreeButtonShown ? tr('instreamWrapper.infoShort') : tr('instreamWrapper.info')
})

const subtext = computed(() => {
    if (props.isAdFreeActive && props.adInfo.adType === 'rewarded') {
        return tr('instreamWrapper.noAdRewarded')
    }

    if (props.adInfo.podCounter) {
        return tr('instreamWrapper.adCounter', {
            count: props.adInfo.podCounter.adPosition,
            total: props.adInfo.podCounter.totalAds,
        })
    }

    if (props.adInfo.secondsLeft > 0) {
        return `${props.adInfo.secondsLeft} ${tr('instreamWrapper.sec')}`
    }

    return ''
})
</script>

<style module>
.wrapper {
    position: fixed;
    inset: 0;
    height: 100svh;
    width: 100vw;
    display: flex;
    visibility: hidden;
    flex-direction: column;
    z-index: 2147483647;
    pointer-events: none;
}

.visible {
    visibility: visible;
}

.visible .ad,
.visible .bottom_bar {
    pointer-events: auto;
}

.ad {
    flex: 1 1 auto;
    position: relative;
    margin: var(--pg-safe-area-top) var(--pg-safe-area-right) 0 var(--pg-safe-area-left);
}

.bottom_bar {
    background: var(--carbon-300);
    flex: 0 0 56px;
    display: flex;
    align-items: center;
    padding: 0 6px;
    gap: 12px;
}

.icon {
    width: 36px;
    height: 36px;
}

.info {
    display: flex;
    flex-direction: column;
    gap: var(--gap-4);
}

.subtext {
    color: var(--carbon-500);
}

.no_ads_button_override {
    margin-left: auto;
}

@media (--mobile) {
    .text {
        font-size: var(--font-size-label-s);
    }

    .subtext {
        font-size: var(--font-size-label-xs);
    }
}
</style>
